import React, { useContext, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FiArrowRight, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import "../css/main.scss";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import { CartContext } from "./CartContext";

const productTabs = [
  { id: "best", label: "Best Sellers" },
  { id: "shampoo", label: "Shampoo" },
  { id: "conditioner", label: "Conditioner" },
  { id: "styling", label: "Stylers" },
  { id: "treatment", label: "Treatments" },
];

const values = [
  "Vegan",
  "Color-Safe",
  "Cruelty-Free",
  "Gluten-Free",
  "Plant-Powered",
  "Sulfate-Free",
];

const hairGoals = [
  {
    title: "Boost Volume",
    text: "Bring body, movement and airy lift to fine strands.",
    query: "volume",
  },
  {
    title: "Define Texture",
    text: "Nourish waves and curls without weighing them down.",
    query: "texture",
  },
  {
    title: "Balance Scalp",
    text: "Refresh roots and restore a comfortable scalp.",
    query: "scalp",
  },
  {
    title: "Preserve Color",
    text: "Protect vibrancy while keeping hair soft and glossy.",
    query: "color",
  },
  {
    title: "Repair & Hydrate",
    text: "Replenish moisture and revive stressed strands.",
    query: "hydrate",
  },
];

const testimonials = [
  {
    quote:
      "My hair feels softer, fuller and visibly shinier. The ritual is simple, but the difference is remarkable.",
    author: "Maya R.",
  },
  {
    quote:
      "These formulas brought life back to my curls without coating them. My wash days have completely changed.",
    author: "Elena S.",
  },
];

const formatPrice = (price) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: siteConfig.currency,
  }).format(Number(price) || 0);

const matchesTab = (product, tab) => {
  const category = String(product.category || "").toLowerCase();

  if (tab === "best") return Boolean(product.bestSeller);
  if (tab === "shampoo") return category.includes("shampoo");
  if (tab === "conditioner") return category.includes("conditioner");
  if (tab === "styling") {
    return category.includes("styler") || category.includes("styling");
  }
  if (tab === "treatment") {
    return category.includes("treatment") || category.includes("mask");
  }

  return true;
};

function HomeProductCard({ added, product, onAdd, onNavigate }) {
  const primaryImage = product.images?.[0] || product.image;
  const secondaryImage = product.images?.[1];

  return (
    <article className="home-product-card">
      <Link
        className="home-product-card__media"
        to={`/shopDetails/${product.id}`}
        onClick={onNavigate}
        aria-label={`View ${product.name}`}
      >
        {product.bestSeller && (
          <span className="home-product-card__badge">Best Seller</span>
        )}
        {product.newArrival && !product.bestSeller && (
          <span className="home-product-card__badge">New</span>
        )}
        <img
          className="home-product-card__image home-product-card__image--primary"
          src={primaryImage}
          alt={product.name}
          loading="lazy"
          decoding="async"
        />
        {secondaryImage && (
          <img
            className="home-product-card__image home-product-card__image--secondary"
            src={secondaryImage}
            alt=""
            loading="lazy"
            decoding="async"
            aria-hidden="true"
          />
        )}
      </Link>
      <div className="home-product-card__details">
        <div
          className="home-product-card__rating"
          aria-label={`${product.rating || 5} out of 5 stars, ${
            product.reviews || 0
          } reviews`}
        >
          <span aria-hidden="true">★★★★★</span>
          <small>{product.rating || "5.0"}</small>
          <small>({product.reviews || 0})</small>
        </div>
        <Link
          className="home-product-card__name"
          to={`/shopDetails/${product.id}`}
          onClick={onNavigate}
        >
          {product.name}
        </Link>
        <p>{product.subtitle}</p>
        <div className="home-product-card__purchase">
          <span>{formatPrice(product.price)}</span>
          <button
            type="button"
            className={added ? "is-added" : ""}
            onClick={() => onAdd(product)}
          >
            {added ? "Added" : "Add to bag"}
          </button>
        </div>
      </div>
    </article>
  );
}

function Main() {
  const [activeTab, setActiveTab] = useState("best");
  const [addedProductId, setAddedProductId] = useState(null);
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const productTrackRef = useRef(null);
  const { addToCart } = useContext(CartContext);
  const brandName = siteConfig.brandName;

  const activeProducts = useMemo(() => {
    const matches = productData.filter((product) =>
      matchesTab(product, activeTab)
    );
    return (matches.length ? matches : productData).slice(0, 10);
  }, [activeTab]);

  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const addProduct = (product) => {
    addToCart?.(
      product.id,
      1,
      product.size?.[0] || "One Size",
      product.colors?.[0] || "Original"
    );
    setAddedProductId(product.id);
    window.setTimeout(() => setAddedProductId(null), 1400);
  };

  const scrollProducts = (direction) => {
    productTrackRef.current?.scrollBy({
      left: direction * productTrackRef.current.clientWidth * 0.78,
      behavior: "smooth",
    });
  };

  const changeTestimonial = (direction) => {
    setTestimonialIndex((current) =>
      (current + direction + testimonials.length) % testimonials.length
    );
  };

  return (
    <div className="main home-page">
      <main>
        <section className="home-hero" aria-labelledby="home-hero-title">
          <img
            className="home-hero__image"
            src="/main/hero.webp"
            alt="Unbranded botanical haircare bottles among tropical foliage"
          />
          <div className="home-hero__shade" />
          <div className="home-hero__content">
            <span className="home-eyebrow">Botanical care, considered daily</span>
            <h1 id="home-hero-title">Your best hair, no heat required</h1>
            <p>
              Effortless, air-dried styles powered by potent botanicals and
              created for every texture.
            </p>
            <Link className="home-button home-button--light" to="/shop" onClick={toTop}>
              Shop the ritual <FiArrowRight aria-hidden="true" />
            </Link>
          </div>
        </section>

        <section className="home-campaign" aria-labelledby="campaign-title">
          <div className="home-campaign__media">
            <img
              src="/main/travel-campaign.webp"
              alt={`${brandName} travel-sized botanical hair care ritual`}
              loading="eager"
            />
          </div>
          <div className="home-campaign__content">
            <span className="home-eyebrow">The travel edit</span>
            <h2 id="campaign-title">A complete routine for your carry-on</h2>
            <p>
              Discover compact, high-performance essentials made to keep hair
              balanced, hydrated and luminous wherever you land.
            </p>
            <Link className="home-text-link" to="/shop?search=travel" onClick={toTop}>
              Shop travel sizes <FiArrowRight aria-hidden="true" />
            </Link>
          </div>
        </section>

        <section className="home-products" aria-labelledby="home-products-title">
          <div className="home-section-heading">
            <div>
              <span className="home-eyebrow">Find your ritual</span>
              <h2 id="home-products-title">Everyday customer favorites</h2>
            </div>
            <div className="home-products__arrows" aria-label="Product carousel controls">
              <button
                type="button"
                aria-label="Previous products"
                onClick={() => scrollProducts(-1)}
              >
                <FiChevronLeft aria-hidden="true" />
              </button>
              <button
                type="button"
                aria-label="Next products"
                onClick={() => scrollProducts(1)}
              >
                <FiChevronRight aria-hidden="true" />
              </button>
            </div>
          </div>

          <div className="home-products__tabs" role="group" aria-label="Choose a product category">
            {productTabs.map((tab) => (
              <button
                type="button"
                key={tab.id}
                aria-pressed={activeTab === tab.id}
                className={activeTab === tab.id ? "is-active" : ""}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="home-products__track" ref={productTrackRef}>
            {activeProducts.map((product) => (
              <HomeProductCard
                key={product.id}
                added={addedProductId === product.id}
                product={product}
                onAdd={addProduct}
                onNavigate={toTop}
              />
            ))}
          </div>

          <Link className="home-button home-button--dark home-products__all" to="/shop" onClick={toTop}>
            Shop all products
          </Link>
        </section>

        <section className="home-values" aria-label="Our product standards">
          <div className="home-values__track">
            {[...values, ...values].map((value, index) => (
              <span key={`${value}-${index}`} aria-hidden={index >= values.length}>
                <i aria-hidden="true">✦</i> {value}
              </span>
            ))}
          </div>
        </section>

        <section className="home-stories" aria-label="Our ingredients and impact">
          <article className="home-story-card">
            <div className="home-story-card__media">
              <img
                src="/main/ingredients.webp"
                alt="Camellia oil, aloe and oat ingredients arranged in a studio"
                loading="lazy"
              />
            </div>
            <div className="home-story-card__content">
              <span className="home-eyebrow">Potent by nature</span>
              <h2>A new understanding of natural beauty</h2>
              <p>
                What matters as much as the ingredients we choose is where they
                come from and how thoughtfully they are gathered.
              </p>
              <Link className="home-text-link" to="/about" onClick={toTop}>
                Explore our ingredients <FiArrowRight aria-hidden="true" />
              </Link>
            </div>
          </article>

          <article className="home-story-card home-story-card--dark">
            <div className="home-story-card__media">
              <img
                src="/main/craft-story.webp"
                alt="Hands sorting botanical ingredients in a bright formulation studio"
                loading="lazy"
              />
            </div>
            <div className="home-story-card__content">
              <span className="home-eyebrow">Beauty with purpose</span>
              <h2>Thoughtful choices from formula to finish</h2>
              <p>
                We choose purposeful ingredients, balanced textures and considered
                packaging to make daily care feel effective without excess.
              </p>
              <Link className="home-text-link" to="/about" onClick={toTop}>
                Read our story <FiArrowRight aria-hidden="true" />
              </Link>
            </div>
          </article>
        </section>

        <section className="home-goals" aria-labelledby="hair-goals-title">
          <div className="home-goals__media">
            <img
              src="/main/hair-goals.webp"
              alt="Five adults with straight, wavy, curly, silver and coily hair textures"
              loading="lazy"
            />
          </div>
          <div className="home-goals__content">
            <span className="home-eyebrow">Made for your hair</span>
            <h2 id="hair-goals-title">Shop by hair goal</h2>
            <p className="home-goals__intro">
              Build a care ritual around what your hair needs today.
            </p>
            <div className="home-goals__list">
              {hairGoals.map((goal, index) => (
                <Link
                  key={goal.title}
                  to={`/shop?search=${goal.query}`}
                  onClick={toTop}
                >
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  <div>
                    <h3>{goal.title}</h3>
                    <p>{goal.text}</p>
                  </div>
                  <FiArrowRight aria-hidden="true" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="home-testimonial" aria-label="Customer testimonials">
          <div className="home-testimonial__media">
            <img
              src="/main/brand-story.webp"
              alt="Hands blending botanical oils in a bright formulation studio"
              loading="lazy"
            />
          </div>
          <div className="home-testimonial__content">
            <span className="home-eyebrow">Real rituals, real results</span>
            <div className="home-testimonial__stars" aria-hidden="true">
              ★★★★★
            </div>
            <blockquote>“{testimonials[testimonialIndex].quote}”</blockquote>
            <cite>— {testimonials[testimonialIndex].author}</cite>
            <div className="home-testimonial__controls">
              <button
                type="button"
                onClick={() => changeTestimonial(-1)}
                aria-label="Previous testimonial"
              >
                <FiChevronLeft aria-hidden="true" />
              </button>
              <span>
                {testimonialIndex + 1} / {testimonials.length}
              </span>
              <button
                type="button"
                onClick={() => changeTestimonial(1)}
                aria-label="Next testimonial"
              >
                <FiChevronRight aria-hidden="true" />
              </button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default Main;
